import {
  DomainCreateDto,
  DomainSecondarySelectDto,
  TechnologySelectDto,
  TechnologyUseCreateDto,
  TechnologyUseSelectDto,
} from '@@types/api.types';
import { Modal, ModalFooterProps } from '@components/core/modal.component';
import { FormLabel } from '@components/shared/form-label.component';
import { PopoverSelector } from '@components/shared/popover-selector.component';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@components/ui/alert-dialog';
import { Button } from '@components/ui/button';
import { Textarea } from '@components/ui/textarea.tsx';
import { useCreateTechnologyUse, useDeleteTechnologyUse, useEditTechnologyUse } from '@queries/technology-uses.query';
import { useGetTechnologyDomains, useGetTechnologyUses } from '@queries/technology.query';
import { getSmallestItemByKey } from '@utils/array.util';
import { mapToSelectOptions } from '@utils/select.util';
import { X } from 'lucide-react';
import { FunctionComponent, useEffect, useState } from 'react';

interface TechnologyUseModalProps {
  isEdit?: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  technology?: TechnologySelectDto;
  totalUses?: number;
  use?: TechnologyUseSelectDto | null;
}

// UPDO: Split this out into a standalong component
//       Just "Badge"
//       Consider making ClickableBadge a derived component
//
const PrimaryBadge: FunctionComponent<{
  disabled?: boolean;
  isPrimary: boolean;
  label: string;
  onClick: () => void;
  showBorder?: boolean;
}> = ({ disabled, isPrimary, label, onClick, showBorder }) => (
  <div
    onClick={disabled ? undefined : onClick}
    className={`text-[14px] px-3 py-1 rounded-sm font-light ${
      isPrimary
        ? 'text-[#35B39C] bg-[#00BD791A]'
        : `text-gray-500 bg-gray-100 border-gray-500 border-solid ${showBorder && 'border'}`
    } ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
  >
    {label}
  </div>
);

export const TechnologyUseModal: FunctionComponent<TechnologyUseModalProps> = (props) => {
  const { isEdit, isOpen, setIsOpen, technology, totalUses, use } = props;
  const [selectedDescription, setSelectedDescription] = useState('');
  const [selectedUse, setSelectedUse] = useState<string | null>(null);
  const [isPrimaryUse, setIsPrimaryUse] = useState<boolean>(false);
  const [selectedDomains, setSelectedDomains] = useState<(DomainCreateDto | null)[]>([]);
  const [primaryDomainIndex, setPrimaryDomainIndex] = useState<number>(0);
  const [subdomains, setSubdomains] = useState<{ [key: number]: DomainSecondarySelectDto[] }>({});
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const { mutate: createTechnologyUse, isPending: isCreatePending } = useCreateTechnologyUse();
  const { mutate: editTechnologyUse, isPending: isEditPending } = useEditTechnologyUse();
  const { mutate: deleteTechnologyUse, isPending: isDeletePending } = useDeleteTechnologyUse();
  const { data: technologyUses } = useGetTechnologyUses();
  const { data: technologyDomains } = useGetTechnologyDomains();

  useEffect(() => {
    if (isOpen) {
      if (isEdit && use) {
        setSelectedDescription(use.description || '');
        setSelectedUse(use.useId || '');
        const domains: DomainCreateDto[] = (use.domains || []).map((domain) => ({
          archived: false,
          archivedAt: undefined,
          id: domain.id,
          isPrimaryDomain: false,
          subdomainIds: domain.subdomainIds ?? [],
        }));

        setSelectedDomains(domains);
        const primaryDomain = use.domains?.length ? getSmallestItemByKey(use.domains, 'position') : undefined;
        setPrimaryDomainIndex(domains.findIndex((domain) => domain.id === primaryDomain?.id));

        // Set primary if it's the only use or if it's the first use in the array
        const isOnlyUse = technology?.uses?.length === 1;
        const isFirstUse = technology?.uses?.[0]?.id === use.id;
        setIsPrimaryUse(isOnlyUse || isFirstUse);

        // Set subdomains for each domain
        const subdomainsMap: { [key: number]: DomainSecondarySelectDto[] } = {};
        domains.forEach((domain, index) => {
          if (domain) {
            subdomainsMap[index] = technologyDomains?.find((d) => d.id === domain.id)?.subdomains || [];
          }
        });
        setSubdomains(subdomainsMap);
      } else {
        setSelectedDescription('');
        setSelectedUse(null);
        setSelectedDomains([null]);
        setPrimaryDomainIndex(0);
        // New use will be primary if there are no existing uses
        setIsPrimaryUse(!technology?.uses?.length);
        setSubdomains({});
      }
    }
  }, [isOpen, isEdit, use, technologyDomains, technology?.uses]);

  useEffect(() => {
    setSelectedDomains((prevDomains) =>
      prevDomains.map((domain, index) => {
        if (domain) {
          return {
            ...domain,
            isPrimaryDomain: index === primaryDomainIndex,
          };
        }
        return domain;
      }),
    );
  }, [primaryDomainIndex]);

  const closeModal = () => {
    setIsOpen(false);
  };

  const addDomain = () => {
    setSelectedDomains([...selectedDomains, null]);
  };

  const updateDomain = (index: number, value: string | null) => {
    const domainObj = value ? technologyDomains?.find((d) => d.id === value) || null : null;
    const newDomains = [...selectedDomains];
    if (domainObj) {
      newDomains[index] = { ...domainObj, isPrimaryDomain: index === primaryDomainIndex, subdomainIds: [] };
      setSubdomains((prevSubdomains) => ({
        ...prevSubdomains,
        [index]: domainObj.subdomains || [],
      }));
    } else {
      newDomains[index] = null;
      setSubdomains((prevSubdomains) => {
        const newSubdomains = { ...prevSubdomains };
        delete newSubdomains[index];
        return newSubdomains;
      });
    }
    setSelectedDomains(newDomains);
  };

  const updateSubdomain = (domainIndex: number, subdomainId: string) => {
    const newDomains = [...selectedDomains];
    if (newDomains[domainIndex]) {
      const currentSubdomainIds = newDomains[domainIndex]?.subdomainIds || [];

      if (subdomainId) {
        // If we have an empty string in the array, replace it with the new ID
        const emptyIndex = currentSubdomainIds.indexOf('');
        if (emptyIndex >= 0) {
          currentSubdomainIds[emptyIndex] = subdomainId;
        } else {
          // Otherwise add the new ID
          currentSubdomainIds.push(subdomainId);
        }
      } else {
        // Add a new empty string to trigger a new select dropdown
        currentSubdomainIds.push('');
      }

      newDomains[domainIndex] = {
        ...newDomains[domainIndex],
        subdomainIds: currentSubdomainIds,
      };
    }
    setSelectedDomains(newDomains);
  };

  const removeSubdomain = (domainIndex: number, subdomainId: string) => {
    const newDomains = [...selectedDomains];
    if (newDomains[domainIndex]) {
      newDomains[domainIndex] = {
        ...newDomains[domainIndex],
        subdomainIds: (newDomains[domainIndex]?.subdomainIds || []).filter((id) => id !== subdomainId),
      };
    }
    setSelectedDomains(newDomains);
  };

  const removeDomain = (index: number) => {
    const newDomains = [...selectedDomains];
    newDomains.splice(index, 1);
    setSelectedDomains(newDomains);

    if (index === primaryDomainIndex) {
      setPrimaryDomainIndex(0);
    } else if (index < primaryDomainIndex) {
      setPrimaryDomainIndex(primaryDomainIndex - 1);
    }

    setSubdomains((prevSubdomains) => {
      const newSubdomains = { ...prevSubdomains };
      delete newSubdomains[index];
      return newSubdomains;
    });
  };

  const saveUse = () => {
    if (!technology || !selectedUse) return;
    const domainsToSubmit = selectedDomains
      .filter((d): d is DomainCreateDto => d !== null)
      .map(({ subdomainIds, ...rest }) => ({
        ...rest,
        subdomainIds: (subdomainIds || []).filter((id) => id !== ''), // Filter out empty strings
      }));

    const technologyUseData: TechnologyUseCreateDto & { id?: string } = {
      useId: selectedUse,
      description: selectedDescription,
      domains: domainsToSubmit,
      isPrimaryUse: isPrimaryUse,
      technologyId: technology.id,
    };

    if (isEdit && use) {
      editTechnologyUse({ ...technologyUseData, id: use.id }, { onSuccess: () => closeModal() });
    } else {
      createTechnologyUse(technologyUseData, { onSuccess: () => closeModal() });
    }
  };

  const getAvailableDomains = (index: number) => {
    return (
      technologyDomains?.map((domain) => ({
        ...domain,
        disabled: selectedDomains.some(
          (selectedDomain, selectedIndex) => selectedDomain?.id === domain.id && selectedIndex !== index,
        ),
      })) || []
    );
  };

  const deleteUse = () => {
    if (!isEdit || !use || !technology) return;
    deleteTechnologyUse(
      {
        technologyUseId: use.id,
        technologyId: technology.id,
      },
      { onSuccess: () => closeModal() },
    );
  };

  const footer: ModalFooterProps = {
    rightButton: {
      disabled: selectedDomains.filter((d) => d !== null).length === 0,
      onClick: saveUse,
      children: isEdit ? 'Update' : 'Add',
      loading: isCreatePending || isEditPending,
    },
    leftButton: {
      onClick: isEdit ? () => setIsAlertOpen(true) : () => setIsOpen(false),
      children: isEdit ? 'Delete' : 'Cancel',
      variant: isEdit ? 'destructive' : 'secondary',
      loading: isDeletePending,
      disabled: isEdit && totalUses === 1,
    },
  };

  return (
    <>
      <Modal
        centered
        footer={footer}
        header={{ title: `${isEdit ? 'Update' : 'Add'} Technology Use` }}
        isOpen={isOpen}
        onClose={() => {
          closeModal();
          setIsOpen(false);
        }}
        width="620px"
      >
        <div className="flex flex-col items-start gap-4 justify-center w-full">
          <div className="flex flex-col w-full">
            <div className="flex flex-col gap-1 w-full">
              <FormLabel
                label="Technology Use"
                isRequired
              />
              <div className="flex items-center space-x-3 mb-4 w-full">
                <PopoverSelector
                  className="w-[50%]"
                  contentClassName="w-[540px]"
                  emptyMessage="No technology use found"
                  name="use"
                  onChange={(e) => {
                    setSelectedUse(e.target.value);
                    const selectedUse = technologyUses?.find((use) => use.id === e.target.value);
                    setSelectedDescription(selectedUse?.description || '');
                  }}
                  options={mapToSelectOptions(
                    technologyUses?.map((use) => ({
                      ...use,
                      disabled:
                        use.id !== selectedUse &&
                        !!technology?.uses?.find((filteredUse) => filteredUse.useId === use.id),
                    })),
                  )}
                  placeholder="Select technology use"
                  searchPlaceholder="Search technology use..."
                  value={selectedUse || ''}
                />
                <PrimaryBadge
                  isPrimary={isPrimaryUse}
                  onClick={() => setIsPrimaryUse(!isPrimaryUse)}
                  label="Primary"
                  disabled={technology?.uses?.length === 1}
                  showBorder={true}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1 w-full">
              <FormLabel
                label="Description"
                className="mb-1"
              />
              <Textarea
                id="description"
                name="description"
                value={selectedDescription}
                onChange={(e) => setSelectedDescription(e.target.value)}
                className="flex h-28 w-full rounded-lg bg-gray-100 p-3"
              />
            </div>
          </div>

          {selectedUse && (
            <>
              <div className="flex flex-col gap-2 w-full items-start">
                <FormLabel
                  label="Domains"
                  isRequired
                />
                <div className="flex flex-col gap-4 w-full">
                  {selectedDomains.map((_domain, index) => (
                    <div
                      key={index}
                      className="flex flex-row rounded-md border border-gray-200"
                    >
                      <div className="flex flex-col gap-4 w-full p-4 pr-0">
                        <div className="flex flex-col w-full">
                          <div className="flex flex-row items-center gap-4 w-full">
                            <PopoverSelector
                              className="w-[357px]"
                              contentClassName="w-[357px]"
                              emptyMessage="No domain found"
                              name="domain"
                              onChange={(e) => updateDomain(index, e.target.value)}
                              options={getAvailableDomains(index)}
                              placeholder="Select domain"
                              searchPlaceholder="Search domain..."
                              value={selectedDomains[index]?.id || ''}
                            />
                            <PrimaryBadge
                              isPrimary={index === primaryDomainIndex}
                              onClick={() => setPrimaryDomainIndex(index)}
                              label="Primary"
                            />
                          </div>
                        </div>
                        {selectedDomains[index]?.id && (
                          <div className="flex flex-col gap-3 border-l border-gray-300 pl-4 items-start">
                            <div className="flex flex-col gap-3 w-full">
                              {selectedDomains[index]?.subdomainIds?.map((subdomainId) => (
                                <div
                                  key={subdomainId || Math.random()}
                                  className="flex flex-row items-center gap-2"
                                >
                                  <PopoverSelector
                                    className="w-[340px]"
                                    contentClassName="w-[340px]"
                                    emptyMessage="No subdomain found"
                                    name="subdomain"
                                    onChange={(e) => updateSubdomain(index, e.target.value || '')}
                                    options={
                                      subdomains[index]?.map((s) => ({
                                        ...s,
                                        disabled:
                                          s.id !== subdomainId &&
                                          !!selectedDomains[index]?.subdomainIds?.includes(s.id),
                                      })) || []
                                    }
                                    placeholder="Select subdomain"
                                    searchPlaceholder="Search subdomain..."
                                    value={subdomainId}
                                  />
                                  {subdomainId && (
                                    <div
                                      onClick={() => removeSubdomain(index, subdomainId)}
                                      className="text-[#1F1C1AB2] cursor-pointer hover:text-gray-600"
                                    >
                                      <X size={16} />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            <Button
                              onClick={() => updateSubdomain(index, '')}
                              variant="link"
                            >
                              + Add Subdomain
                            </Button>
                          </div>
                        )}
                      </div>
                      <div
                        onClick={() => removeDomain(index)}
                        className="p-2 text-[#1F1C1AB2] cursor-pointer"
                      >
                        <X size={16} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Button
                onClick={addDomain}
                variant="link"
              >
                + Add Domain
              </Button>
            </>
          )}
        </div>
      </Modal>
      {isEdit && (
        <AlertDialog
          open={isAlertOpen}
          onOpenChange={setIsAlertOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the technology use.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteUse}
                variant="danger"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};
